import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  LocalizationProvider,
  TimePicker,
  renderTimeViewClock,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { FormHelperText } from '@mui/material';
import StyledCommonInputWrapper from '../../../../style/Common/Controller/Common/StyledCommonInputWrapper';
import StyledCommonLabel from '../../../../style/Common/Controller/Common/StyledCommonLabel';
import StyledCommonInputRequiredLabel from '../../../../style/Common/Controller/Common/StyledCommonInputRequiredLabel';
import createDefaultInputRules from '../../../../util/Common/Controller/Generator/createDefaultInputRules';

const CommonTime = ({ ...props }) => {
  /**
   * @description
   * Get react-hook-form object from useFormContext.
   * @control Must-have elements for getting values from a form.
   * @unregister Allows to unregister a single input or an array of inputs.
   * @setValue Setter value of input.
   * @formState Object containing information about the form.
   */
  const {
    control,
    unregister,
    setValue,
    formState: { errors: formErrors },
  } = useFormContext();

  /**
   * @label {string} - Label of input.
   * @required {boolean} - Required input.
   * @disabled {boolean} - Disable input.
   * @defaultValue {any} - Default value of input.
   */
  const [label, setLabel] = useState(props?.label);

  // Set default required as false
  const [required, setRequired] = useState(props?.required || false);
  useEffect(() => {
    /**
     * If default required value changed, component required value would be changed too.
     * Undefined, Null exception handling.
     */
    if (props?.required !== undefined && props?.required !== null) {
      setRequired(props?.required);
    }
  }, [props?.required]);

  // Set default disabled as false
  const [disabled, setDisabled] = useState(props?.disabled || false);
  useEffect(() => {
    /**
     * If default disabled value changed, component disabled value would be changed too.
     * Undefined, Null exception handling.
     */
    if (props?.disabled !== undefined && props?.disabled !== null) {
      setDisabled(props?.disabled);
    }
  }, [props?.disabled]);

  // Set default input value as null.
  const [defaultValue, setDefaultValue] = useState(props?.defaultValue || null);
  useEffect(() => {
    /**
     * If default value changed, component default value would be changed too.
     * Undefined exception handling. Null allowed value.
     */
    if (props?.defaultValue !== undefined) {
      setDefaultValue(props?.defaultValue);
      setValue(props?.inputName, props?.defaultValue);
    }
  }, [
    props?.defaultValue && dayjs(props?.defaultValue)?.isValid()
      ? dayjs(props?.defaultValue)?.format('HH:mm')
      : null,
  ]);

  useEffect(() => {
    /**
     * Unregister input when component unmount.
     * Unregister single input only.
     */
    return () => unregister(props?.inputName);
  }, []);

  /**
   * @description
   * Render Date input component.
   * @control - Must-have elements for getting values from a form.
   * @name - Name of input
   * @defaultValue - Default value of input.
   * @rules - Rules of input.
   * @render - Render target input component.
   * - field : Object containing field props, like name, value, onChange, onBlur.
   * - fieldState : Object containing field state, like error, invalid, etc.
   */
  return (
    <Controller
      control={control}
      name={props?.inputName}
      defaultValue={
        defaultValue && dayjs(defaultValue).isValid()
          ? dayjs(defaultValue)
          : null
      }
      rules={createDefaultInputRules({
        required,
        ...props,
      })}
      render={({
        field: { ref, onChange, value, ...field },
        fieldState: { invalid, error },
      }) => {
        // Package of handler props
        const handlerProps = {
          onChange,
          value,
          ...field,
          ...props,
        };
        // Render Control Input Component
        return (
          <>
            <StyledCommonInputWrapper labelStyle={props?.labelStyle}>
              {(!!label || props.labelVisible !== false) && (
                <StyledCommonLabel
                  labelStyle={props?.labelStyle}
                  lbl-pd-right={props?.[`lbl-pd-right`]}
                >
                  {!!label && (
                    <>
                      {!!required && (
                        <StyledCommonInputRequiredLabel>
                          *
                        </StyledCommonInputRequiredLabel>
                      )}
                      {label}
                    </>
                  )}
                </StyledCommonLabel>
              )}
              <div className={`${props?.width ? `${props?.width}` : 'w-full'}`}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    {...field}
                    inputRef={ref}
                    error={invalid}
                    format="HH:mm"
                    ampm={false}
                    value={
                      value && dayjs(value)?.isValid() ? dayjs(value) : null
                    }
                    /**
                     * onChange Handler. Only handlers of parameters work.
                     * To change value, should use Controller field's onChange changer.
                     * ex)
                     * (tempValue) => {
                     *    onChange(tempValue);
                     * }
                     */
                    onChange={e =>
                      props?.onChangeHandler?.({ e, ...handlerProps })
                    }
                    disabled={disabled}
                    className={`${
                      disabled ? 'bg-[#F9F9F9]' : 'bg-white'
                    } rounded-[5px]`}
                    slotProps={{
                      popper: {
                        sx: {
                          '&.MuiPickersPopper-root': {
                            zIndex: '10000 !important',
                          },
                        },
                      },
                      textField: {
                        error: invalid,
                        sx: {
                          svg: { color: !disabled && '#264B9F' },
                          '&.MuiFormControl-root': {
                            width: '100%',
                          },
                          '.MuiInputBase-root': {
                            height: '30px',
                            borderRadius: '5px',
                            fontSize: '12px',
                            fontWeight: '500',
                            '&:not(.Mui-focused):not(.Mui-disabled):hover fieldset':
                              !formErrors?.[props?.inputName]?.message && {
                                borderColor: '#8E9396 !important',
                              },
                            '&.Mui-disabled input': {
                              WebkitTextFillColor: `#222 !important`,
                            },
                            '&.Mui-focused fieldset': {
                              border: '1.2px solid #264B9F !important',
                              boxShadow: '0px 0px 4px 0px #8BBCE9 !important',
                            },
                            '&.Mui-error fieldset': {
                              border: '1.2px solid #C24D4D !important',
                            },
                          },
                          '.MuiButtonBase-root': {
                            paddingLeft: '4px',
                          },
                          '.MuiSvgIcon-root': {
                            fontSize: '16px',
                          },
                          fieldset: {
                            borderColor: '#D9D9D9 !important',
                          },
                        },
                      },
                    }}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                    }}
                  />
                </LocalizationProvider>
              </div>
            </StyledCommonInputWrapper>
            <FormHelperText className="text-[#ff2424] text-[11px] font-normal min-h-[11px] h-[11px] mb-[3px] leading-none">
              {!props?.suppressErrorVisible && error?.message}
            </FormHelperText>
          </>
        );
      }}
    />
  );
};

export default CommonTime;
